import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefCallback,
} from "react";
import {
  selectActiveScrollContainer,
  type ScrollContainerClaim,
} from "@/contexts/scrollContainerRegistry";

export const SCROLL_CONTAINER_PRIORITY = {
  TAB: 0,
  PANEL: 10,
} as const;
export const SCROLL_CONTAINER_OVERLAY_LAYER_STEP = 100;

type ScrollContainerContextValue = {
  activeScrollContainer: HTMLElement | null;
  registerScrollContainer: (container: HTMLElement, priority: number) => () => void;
};

const ScrollContainerContext = createContext<ScrollContainerContextValue | null>(null);
const ScrollContainerLayerContext = createContext(0);

export function ScrollContainerProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<ScrollContainerClaim<HTMLElement>[]>([]);
  const nextOrderRef = useRef(0);

  const registerScrollContainer = useCallback((container: HTMLElement, priority: number) => {
    const token = Symbol("scroll-container");
    nextOrderRef.current += 1;
    const nextClaim: ScrollContainerClaim<HTMLElement> = {
      token,
      container,
      priority,
      order: nextOrderRef.current,
    };
    let released = false;

    setClaims(current => [...current, nextClaim]);

    return () => {
      if (released) return;
      released = true;
      setClaims(current => current.filter(claim => claim.token !== token));
    };
  }, []);

  const activeScrollContainer = selectActiveScrollContainer(claims);

  const value = useMemo(
    () => ({
      activeScrollContainer,
      registerScrollContainer,
    }),
    [activeScrollContainer, registerScrollContainer],
  );

  return <ScrollContainerContext.Provider value={value}>{children}</ScrollContainerContext.Provider>;
}

export function useScrollContainer() {
  return useContext(ScrollContainerContext);
}

export function useActiveScrollContainer() {
  return useContext(ScrollContainerContext)?.activeScrollContainer ?? null;
}

export function ScrollContainerLayerProvider({
  layer,
  children,
}: {
  layer: number;
  children: ReactNode;
}) {
  return (
    <ScrollContainerLayerContext.Provider value={layer}>
      {children}
    </ScrollContainerLayerContext.Provider>
  );
}

export function useScrollContainerRegistration(
  isActive: boolean,
  priority: number = SCROLL_CONTAINER_PRIORITY.TAB,
): RefCallback<HTMLElement> {
  const registerScrollContainer = useContext(ScrollContainerContext)?.registerScrollContainer;
  const inheritedLayer = useContext(ScrollContainerLayerContext);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const resolvedPriority = inheritedLayer + priority;

  const setScrollContainerRef = useCallback((nextContainer: HTMLElement | null) => {
    setContainer(current => current === nextContainer ? current : nextContainer);
  }, []);

  useEffect(() => {
    if (!isActive || !container || !registerScrollContainer) return;

    const { overflowY } = window.getComputedStyle(container);
    if (overflowY !== "auto" && overflowY !== "scroll") return;

    return registerScrollContainer(container, resolvedPriority);
  }, [container, isActive, registerScrollContainer, resolvedPriority]);

  return setScrollContainerRef;
}
